// src/services/discord.js

const DISCORD_WEBHOOK_URL = process.env.REACT_APP_DISCORD_WEBHOOK_URL || '';

export const sendBetToDiscord = async (bet, user, userInfo) => {
  if (!DISCORD_WEBHOOK_URL) {
    console.warn('Discord webhook URL not configured');
    return;
  }

  // Only send public bets from tipsters
  if (!bet.isPublic || !bet.isTipsterBet) {
    return;
  }

  try {
    const embedColor = 
      bet.confidence === 'high' ? 0x00ff00 : // Green
      bet.confidence === 'medium' ? 0xffff00 : // Yellow
      0xff9500; // Orange for low

    const tipsterName = bet.tipster || user?.displayName || user?.email || 'Anonymous';
    
    // Format the bet details
    const betDetails = bet.isMultiLeg 
      ? bet.legs.map((leg, index) => `**Leg ${index + 1}:** ${leg.match}\n${leg.bet} @ ${leg.odds}`).join('\n\n')
      : `**Match:** ${bet.match}\n**Selection:** ${bet.bet}`;

    const eventTime = bet.eventDateTime 
      ? new Date(bet.eventDateTime).toLocaleString('en-US', { 
          weekday: 'short', 
          month: 'short', 
          day: 'numeric', 
          hour: '2-digit', 
          minute: '2-digit' 
        })
      : 'TBA';

    const embed = {
      embeds: [{
        title: `🎯 New ${bet.confidence.toUpperCase()} Confidence Tip`,
        color: embedColor,
        author: {
          name: `${tipsterName} ${userInfo?.membershipTier === 'diamond' ? '💎' : userInfo?.membershipTier === 'gold' ? '🥇' : ''}`,
          icon_url: 'https://cdn.discordapp.com/embed/avatars/0.png'
        },
        fields: [
          {
            name: '🏆 Sport',
            value: bet.sport + (bet.league ? ` - ${bet.league}` : ''),
            inline: true
          },
          {
            name: '💰 Odds',
            value: bet.odds.toString(),
            inline: true
          },
          {
            name: '📊 Units',
            value: `${bet.units || '1'} unit${parseFloat(bet.units || 1) !== 1 ? 's' : ''}`,
            inline: true
          },
          {
            name: '📅 Event Time',
            value: eventTime,
            inline: true
          },
          {
            name: bet.isMultiLeg ? '🎲 Multi-Leg Details' : '🎲 Bet Details',
            value: betDetails,
            inline: false
          }
        ],
        footer: {
          text: `PureDegen Beta • ${bet.isMultiLeg ? `${bet.legs.length}-leg multi` : 'Single bet'}`,
          icon_url: 'https://cdn.discordapp.com/embed/avatars/0.png'
        },
        timestamp: new Date().toISOString()
      }]
    };

    // Add analysis if provided
    if (bet.analysis) {
      embed.embeds[0].fields.push({
        name: '💭 Analysis',
        value: bet.analysis.substring(0, 1024), // Discord field limit
        inline: false
      });
    }

    const response = await fetch(DISCORD_WEBHOOK_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(embed)
    });

    if (!response.ok) {
      console.error('Failed to send to Discord:', response.status);
    }
  } catch (error) {
    console.error('Error sending bet to Discord:', error);
  }
};

export const sendBetResultToDiscord = async (bet, result) => {
  if (!DISCORD_WEBHOOK_URL || !bet.isPublic || !bet.isTipsterBet) {
    return;
  }

  try {
    const embedColor = 
      result === 'won' ? 0x00ff00 : // Green
      result === 'lost' ? 0xff0000 : // Red
      0x808080; // Gray for push

    const resultEmoji = 
      result === 'won' ? '✅' :
      result === 'lost' ? '❌' :
      '➖';

    const tipsterName = bet.tipster || 'Anonymous';
    
    const profit = result === 'won' 
      ? `+${((parseFloat(bet.units || 1) * parseFloat(bet.odds)) - parseFloat(bet.units || 1)).toFixed(2)}u`
      : result === 'lost'
      ? `-${parseFloat(bet.units || 1).toFixed(2)}u`
      : '0u';

    const embed = {
      embeds: [{
        title: `${resultEmoji} Bet Result: ${result.toUpperCase()}`,
        color: embedColor,
        author: {
          name: tipsterName,
          icon_url: 'https://cdn.discordapp.com/embed/avatars/0.png'
        },
        fields: [
          {
            name: '🏆 Sport',
            value: bet.sport,
            inline: true
          },
          {
            name: '🎯 Match',
            value: bet.match,
            inline: true
          },
          {
            name: '💰 Profit/Loss',
            value: profit,
            inline: true
          },
          {
            name: '📊 Bet Details',
            value: `${bet.bet} @ ${bet.odds}`,
            inline: false
          }
        ],
        footer: {
          text: 'PureDegen Beta',
          icon_url: 'https://cdn.discordapp.com/embed/avatars/0.png'
        },
        timestamp: new Date().toISOString()
      }]
    };

    if (bet.closingLine) {
      embed.embeds[0].fields.push({
        name: '📈 Closing Line',
        value: bet.closingLine,
        inline: true
      });
    }

    await fetch(DISCORD_WEBHOOK_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(embed)
    });
  } catch (error) {
    console.error('Error sending result to Discord:', error);
  }
};